import React, { useCallback, useEffect, useState, createContext, useContext } from 'react';

interface ThemeContextType {
  isDark: boolean;
  toggle: () => void;
}

const ThemeContext = createContext<ThemeContextType>({
  isDark: true,
  toggle: () => {},
});

export function ThemeProvider({ children }: { children: React.ReactNode }) {
  // Initialize theme from localStorage (or system preference)
  const [isDark, setIsDark] = useState(() => {
    if (typeof window !== 'undefined') {
      const saved = localStorage.getItem('theme');
      if (saved) return saved === 'dark';
      // fallback to system preference
      return window.matchMedia('(prefers-color-scheme: dark)').matches;
    }
    return true;
  });

  const toggle = useCallback(() => {
    setIsDark((prev) => {
      const next = !prev;
      if (typeof window !== 'undefined') {
        localStorage.setItem('theme', next ? 'dark' : 'light');
        
        // CRITICAL FIX: Apply class BEFORE state update to prevent flash
        const root = document.documentElement;
        
        // Remove transition temporarily for instant color change
        root.style.transition = 'none';
        
        if (next) {
          root.classList.add('dark');
          root.classList.remove('light');
        } else {
          root.classList.remove('dark');
          root.classList.add('light');
        }
        
        // Force reflow to apply changes
        void root.offsetHeight;
        
        // Re-enable transitions for animations (but not colors)
        setTimeout(() => {
          root.style.transition = '';
        }, 0);
      }
      return next;
    });
  }, []);

  // On mount, apply correct theme immediately
  useEffect(() => {
    const root = document.documentElement;
    
    // Apply theme instantly without transition
    root.style.transition = 'none';
    
    if (isDark) {
      root.classList.add('dark');
      root.classList.remove('light');
    } else {
      root.classList.remove('dark');
      root.classList.add('light');
    }
    
    // Force reflow
    void root.offsetHeight;
    
    // Re-enable transitions
    setTimeout(() => {
      root.style.transition = '';
    }, 0);
  }, []); // only once

  return (
    <ThemeContext.Provider value={{ isDark, toggle }}>
      {children}
    </ThemeContext.Provider>
  );
}

export function useTheme() {
  return useContext(ThemeContext);
}